import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import { RippleModule } from 'primeng/ripple';
import { TranslateModule } from '@ngx-translate/core';
import { ExcelService } from '@fitness-force/api';
import type { ClientFilter } from '@fitness-force/models';
import * as XLSX from 'xlsx';

export type ClientFilterType = 'none' | 'include' | 'exclude';

export interface ClientFilterOutput {
  filterType: ClientFilterType;
  clientIds: number[];
}

@Component({
  selector: 'app-client-filter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    TextareaModule,
    TooltipModule,
    RippleModule,
    TranslateModule,
  ],
  templateUrl: './client-filter.component.html',
  styleUrls: ['./client-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientFilterComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly excelService = inject(ExcelService);
  private readonly cdr = inject(ChangeDetectorRef);

  // Inputs
  readonly initialFilterType = input<ClientFilterType>('none');
  readonly initialClientIds = input<number[]>([]);
  readonly disabled = input<boolean>(false);

  // Outputs
  readonly filterChange = output<ClientFilterOutput>();
  readonly validityChange = output<boolean>();

  // Signals for uploaded IDs (separate from manual textarea)
  includeUploadedIds = signal<number[]>([]);
  excludeUploadedIds = signal<number[]>([]);
  includeUploadError = signal<string>('');
  excludeUploadError = signal<string>('');

  // Form
  clientFilterForm!: FormGroup;

  ngOnInit(): void {
    this.initializeForm();
    this.setupFormListeners();
    this.loadInitialValues();
    setTimeout(() => this.emitValidity(), 0);
  }

  private initializeForm(): void {
    this.clientFilterForm = this.fb.group({
      includeIds: ['', [this.clientIdsValidator('include').bind(this)]],
      excludeIds: ['', [this.clientIdsValidator('exclude').bind(this)]],
    });
  }

  private setupFormListeners(): void {
    this.clientFilterForm.get('includeIds')?.valueChanges.subscribe(() => {
      this.emitChanges();
      this.emitValidity();
    });
    this.clientFilterForm.get('excludeIds')?.valueChanges.subscribe(() => {
      this.emitChanges();
      this.emitValidity();
    });
  }

  private loadInitialValues(): void {
    const initialType = this.initialFilterType();
    const initialIds = this.initialClientIds();
    if (initialType === 'include' && initialIds.length > 0) {
      this.clientFilterForm.patchValue({ includeIds: initialIds.join(',') }, { emitEvent: false });
    } else if (initialType === 'exclude' && initialIds.length > 0) {
      this.clientFilterForm.patchValue({ excludeIds: initialIds.join(',') }, { emitEvent: false });
    }
    this.emitValidity();
  }

  // ─── Validators ────────────────────────────────────────────────────────────

  private clientIdsValidator(_field: 'include' | 'exclude') {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value?.trim();
      if (!value) return null; // optional — empty means no filter for this side

      if (value.includes(';')) return { invalidSeparator: true };

      const parts = value.split(',').map((p: string) => p.trim()).filter((p: string) => p !== '');
      if (parts.length === 0) return null;

      const numbers: number[] = [];
      for (const part of parts) {
        if (!/^\d+$/.test(part)) return { invalidFormat: true };
        const num = parseInt(part, 10);
        if (num < 0) return { negativeNumber: true };
        if (numbers.includes(num)) return { duplicates: true };
        numbers.push(num);
      }
      return null;
    };
  }

  // ─── Excel upload ───────────────────────────────────────────────────────────

  triggerUpload(type: 'include' | 'exclude'): void {
    const inputId = type === 'include' ? 'include-excel-upload' : 'exclude-excel-upload';
    document.getElementById(inputId)?.click();
  }

  onFileSelected(event: Event, type: 'include' | 'exclude'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet);

        const ids: number[] = [];
        for (const row of rows) {
          const rawVal = row['Client Id'] ?? row['client_id'] ?? row['ClientId'] ?? row['client id'];
          if (rawVal !== undefined && rawVal !== null && rawVal !== '') {
            const num = parseInt(String(rawVal), 10);
            if (!isNaN(num) && num > 0 && !ids.includes(num)) {
              ids.push(num);
            }
          }
        }

        if (ids.length === 0) {
          if (type === 'include') this.includeUploadError.set('CAMPAIGN.CLIENT_FILTER.UPLOAD_NO_IDS');
          else this.excludeUploadError.set('CAMPAIGN.CLIENT_FILTER.UPLOAD_NO_IDS');
        } else {
          if (type === 'include') {
            this.includeUploadError.set('');
            this.includeUploadedIds.set(ids);
            // Merge with manual textarea
            this.mergeIdsIntoControl('includeIds', ids);
          } else {
            this.excludeUploadError.set('');
            this.excludeUploadedIds.set(ids);
            this.mergeIdsIntoControl('excludeIds', ids);
          }
        }
      } catch {
        const errKey = 'CAMPAIGN.CLIENT_FILTER.UPLOAD_ERROR';
        if (type === 'include') this.includeUploadError.set(errKey);
        else this.excludeUploadError.set(errKey);
      }

      // Reset file input so same file can be re-uploaded
      input.value = '';
      this.cdr.detectChanges();
    };
    reader.readAsArrayBuffer(file);
  }

  private mergeIdsIntoControl(controlName: string, newIds: number[]): void {
    const control = this.clientFilterForm.get(controlName);
    const existing = this.parseClientIds(control?.value ?? '');
    const merged = Array.from(new Set([...existing, ...newIds]));
    control?.setValue(merged.join(','), { emitEvent: true });
    control?.markAsTouched();
  }

  // ─── Download sample Excel ──────────────────────────────────────────────────

  downloadSampleExcel(): void {
    const sampleData = [
      { 'Client Id': 1001 },
      { 'Client Id': 1002 },
      { 'Client Id': 1003 },
    ];
    this.excelService.exportAsExcelFile(sampleData, 'client_ids_sample', 'xlsx');
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  private parseClientIds(value: string): number[] {
    if (!value?.trim()) return [];
    return value
      .split(',')
      .map((p) => p.trim())
      .filter((p) => p !== '' && /^\d+$/.test(p))
      .map((p) => parseInt(p, 10))
      .filter((n) => !isNaN(n) && n >= 0);
  }

  getIdCount(controlName: string): number {
    const val = this.clientFilterForm.get(controlName)?.value ?? '';
    return this.parseClientIds(val).length;
  }

  onIdsBlur(controlName: string): void {
    const control = this.clientFilterForm.get(controlName);
    const value = control?.value;
    if (value) {
      const cleaned = value
        .split(',')
        .map((p: string) => p.trim())
        .filter((p: string) => p !== '' && /^\d+$/.test(p))
        .join(',');
      if (cleaned !== value) control?.setValue(cleaned, { emitEvent: true });
    }
    control?.markAsTouched();
    control?.updateValueAndValidity();
    this.emitValidity();
  }

  // ─── Emit helpers ───────────────────────────────────────────────────────────

  private emitChanges(): void {
    const includeIds = this.parseClientIds(this.clientFilterForm.get('includeIds')?.value ?? '');
    const excludeIds = this.parseClientIds(this.clientFilterForm.get('excludeIds')?.value ?? '');

    // Determine dominant filter type for backward-compat output
    let filterType: ClientFilterType = 'none';
    let clientIds: number[] = [];

    if (includeIds.length > 0 && excludeIds.length === 0) {
      filterType = 'include';
      clientIds = includeIds;
    } else if (excludeIds.length > 0 && includeIds.length === 0) {
      filterType = 'exclude';
      clientIds = excludeIds;
    } else if (includeIds.length > 0 && excludeIds.length > 0) {
      // Both set — emit include; parent buildClientFilter handles both via getFilterData()
      filterType = 'include';
      clientIds = includeIds;
    }

    this.filterChange.emit({ filterType, clientIds });
  }

  private emitValidity(): void {
    this.validityChange.emit(this.clientFilterForm.valid);
  }

  isFormValid(): boolean {
    return this.clientFilterForm.valid;
  }

  /**
   * Returns full ClientFilter with both include and exclude arrays for payload
   */
  getFilterData(): ClientFilter | null {
    const includeIds = this.parseClientIds(this.clientFilterForm.get('includeIds')?.value ?? '');
    const excludeIds = this.parseClientIds(this.clientFilterForm.get('excludeIds')?.value ?? '');

    if (includeIds.length === 0 && excludeIds.length === 0) return null;

    const filter: ClientFilter = {};
    if (includeIds.length > 0) filter.include_client_ids = includeIds;
    if (excludeIds.length > 0) filter.exclude_client_ids = excludeIds;
    return filter;
  }

  // Getters for template
  get includeControl(): AbstractControl | null {
    return this.clientFilterForm.get('includeIds');
  }

  get excludeControl(): AbstractControl | null {
    return this.clientFilterForm.get('excludeIds');
  }
}
